import api from '../api';
import { unwrapList } from '../unwrapList';
import { taskPollingService } from './taskPollingService';
import type { AnalysisResult, GenerationResponse } from './types';

// Brandbook/Moodboard Analysis
const analyzeBrandbook = async (
    imageFiles: File[],
    textContent: string[],
    type: 'brandbook' | 'moodboard',
    updateBrandbookId?: number,
    enrichMode: boolean = false
): Promise<AnalysisResult> => {
    const formData = new FormData();
    imageFiles.forEach((file) => {
        formData.append('images[]', file);
    });
    textContent.forEach((text) => {
        formData.append('texts[]', text);
    });

    // Add update_brandbook_id if provided (for updating existing brandbook)
    if (updateBrandbookId !== undefined && updateBrandbookId !== null) {
        formData.append('update_brandbook_id', updateBrandbookId.toString());
        formData.append('enrich_mode', enrichMode ? '1' : '0');
    }

    const response = await api.post(`/ai/brandbook/${type}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    const data = response.data as GenerationResponse;
    const completedTask = await taskPollingService.pollTask(data.taskId);

    const rawAnalysis = completedTask.analysisResults || completedTask.metadata?.analysisResult || {};

    // Extract brandbookId from task (axios converts snake_case to camelCase)
    const brandbookId = (completedTask as any).brandbookId || (completedTask.metadata as any)?.brandbookId || null;

    // Map backend response to frontend expected format
    const analysis = {
        ...rawAnalysis,
        // Map 'colors' to 'colorPalette' if needed
        colorPalette: rawAnalysis.colors || rawAnalysis.colorPalette || [],
        // Map 'visual_identity' to 'stylePrompt' - Use photography recommendations if available
        stylePrompt: rawAnalysis.photographyRecommendations?.styleSummary ||
            (typeof rawAnalysis.visualIdentity === 'string' ? rawAnalysis.visualIdentity : JSON.stringify(rawAnalysis.visualIdentity || '')),
        // Generate moodboardPrompt if missing
        moodboardPrompt: rawAnalysis.moodboardPrompt ||
            `Create a moodboard reflecting: ${JSON.stringify(rawAnalysis.brandIdentity?.coreValues || rawAnalysis.brandValues || '')} ${JSON.stringify(rawAnalysis.productPhotographyStyle?.overallAesthetic || rawAnalysis.visualIdentity || '')}`
    };

    return {
        analysis: analysis,
        usage: completedTask.tokenUsage,
        brandbook_id: brandbookId // Return brandbook_id if it was created automatically
    };
};

// Check if Brandbook URL exists
const checkBrandbookUrl = async (url: string): Promise<{ exists: boolean; brandbook?: any }> => {
    const response = await api.post('/ai/brandbooks/check', { url });
    return (response.data as any);
};

// Analyze Brandbook from URL
const analyzeUrlForBrandbook = async (url: string, updateBrandbookId?: number, enrichMode: boolean = false): Promise<AnalysisResult> => {
    try {
        // Send URL to backend for analysis
        const payload: any = { url };
        if (updateBrandbookId !== undefined && updateBrandbookId !== null) {
            payload.update_brandbook_id = updateBrandbookId;
            payload.enrich_mode = enrichMode;
        }

        const response = await api.post('/ai/brandbook/brandbook', payload);
        const { taskId } = (response.data as any);

        // Use WebSockets for real-time updates
        const task = await taskPollingService.listenToTask(taskId);

        if (task.status === 'failed') {
            throw new Error(task.errorMessage || 'Analysis failed');
        }

        const rawAnalysis = task.metadata?.analysisResult || task.analysisResults || {};
        const brandbookId = (task as any).brandbookId || (task.metadata as any)?.brandbookId || null;

        return {
            ...rawAnalysis,
            brandbook_id: brandbookId,
        } as any;
    } catch (error) {
        console.error("Error analyzing brandbook from URL:", error);
        throw error;
    }
};

// Analyze Image for Brandbook (for enrichment with uploaded image)
const analyzeImageForBrandbook = async (image: File, updateBrandbookId?: number, enrichMode: boolean = false): Promise<AnalysisResult> => {
    try {
        const formData = new FormData();
        formData.append('image', image);

        if (updateBrandbookId !== undefined && updateBrandbookId !== null) {
            formData.append('update_brandbook_id', updateBrandbookId.toString());
            formData.append('enrich_mode', enrichMode ? '1' : '0');
        }

        const response = await api.post('/ai/brandbook/brandbook', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const { taskId } = (response.data as any);

        // Use WebSockets for real-time updates
        const task = await taskPollingService.listenToTask(taskId);

        if (task.status === 'failed') {
            throw new Error(task.errorMessage || 'Analysis failed');
        }

        return task.metadata?.analysisResult || task.analysisResults;
    } catch (error) {
        console.error("Error analyzing brandbook from image:", error);
        throw error;
    }
};

// New method for multiple images
const analyzeImagesForBrandbook = async (images: File[], updateBrandbookId?: number, enrichMode: boolean = false): Promise<AnalysisResult> => {
    try {
        const formData = new FormData();

        // Append all images
        images.forEach((image, index) => {
            formData.append(`images[${index}]`, image);
        });

        formData.append('analysis_type', 'brandbook');

        if (updateBrandbookId !== undefined && updateBrandbookId !== null) {
            formData.append('update_brandbook_id', updateBrandbookId.toString());
            formData.append('enrich_mode', enrichMode ? '1' : '0');
        }

        const response = await api.post('/ai/brandbook/multiple', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const { taskId } = (response.data as any);

        // Use WebSockets for real-time updates
        const task = await taskPollingService.listenToTask(taskId);

        if (task.status === 'failed') {
            throw new Error(task.errorMessage || 'Analysis failed');
        }

        return task.metadata?.analysisResult || task.analysisResults;
    } catch (error) {
        console.error("Error analyzing brandbook from multiple images:", error);
        throw error;
    }
};

// Create Brandbook/Style
const createBrandbook = async (data: {
    name?: string;
    url?: string | null;
    style_prompt: string;
    color_palette?: string[];
    moodboard_images?: any[];
    type?: 'brandbook' | 'style' | 'moodboard';
}): Promise<any> => {
    const response = await api.post('/ai/brandbooks', data);
    return (response.data as any).data;
};

// Update Brandbook (name, style_prompt, color_palette)
const updateBrandbook = async (id: number | string, data: {
    name?: string;
    style_prompt?: string;
    color_palette?: string[];
}): Promise<any> => {
    const response = await api.patch(`/ai/brandbooks/${id}`, data);
    return (response.data as any).data;
};

// Delete Brandbook/Style
const deleteBrandbook = async (id: string): Promise<void> => {
    await api.delete(`/ai/brandbooks/${id}`);
};

// RAG & Brand Intelligence
const saveBrandbook = async (brandbook: any): Promise<any> => {
    const response = await api.post('/ai/brandbooks', brandbook);
    return (response.data as any).data;
};

const getBrandbooks = async (): Promise<any[]> => {
    const response = await api.get('/ai/brandbooks');
    return unwrapList(response.data);
};

export const brandbookService = {
    analyzeBrandbook,
    checkBrandbookUrl,
    analyzeUrlForBrandbook,
    analyzeImageForBrandbook,
    analyzeImagesForBrandbook,
    createBrandbook,
    updateBrandbook,
    deleteBrandbook,
    saveBrandbook,
    getBrandbooks,
};
